// The WEB engines behind the shared voice seams (Brief 3 D7) — the browser's
// Web Speech API: `SpeechRecognition` (dictation; Chrome ships it prefixed
// as `webkitSpeechRecognition`) and `speechSynthesis` (listen). This is the
// web host's half of the seam, exactly as `webTokenStore` is for storage:
// the shared layer defines the contract; this module touches `window`.
//
// Availability is a live check, so a browser with neither engine simply
// renders no mic / no listen buttons (the graceful-absence rule). The listen
// engine is local TTS for v1 — free, offline, instant; the seam shape lets
// the Fal voice (the email reports' voice) replace it later, config-gated.

import type { DictationSeam, ListenSeam } from "@waytide/core";

/** The browser's recognition constructor, prefixed or not, when present. */
type RecognitionCtor = new () => {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>; resultIndex: number }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
};

function recognitionCtor(): RecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const webDictation: DictationSeam = {
  available: () => typeof window !== "undefined" && recognitionCtor() !== null,
  start(onText, onEnd) {
    const Ctor = recognitionCtor();
    if (!Ctor) {
      onEnd();
      return () => {};
    }
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = false; // finalized fragments only, per the seam
    rec.lang = navigator.language || "en-US";
    rec.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal && result[0]) onText(result[0].transcript);
      }
    };
    let ended = false;
    const end = () => {
      if (!ended) {
        ended = true;
        onEnd();
      }
    };
    rec.onend = end;
    rec.onerror = end; // an engine error ends the session calmly
    try {
      rec.start();
    } catch {
      end(); // double-start etc. — never throw into the composer
    }
    return () => {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    };
  },
};

export const webListen: ListenSeam = {
  available: () =>
    typeof window !== "undefined" && "speechSynthesis" in window,
  speak(text, onEnd) {
    const synth = window.speechSynthesis;
    const utterance = new SpeechSynthesisUtterance(text);
    let done = false;
    const end = () => {
      if (!done) {
        done = true;
        onEnd();
      }
    };
    utterance.onend = end;
    utterance.onerror = end;
    synth.cancel(); // one voice at a time, engine-side too
    synth.speak(utterance);
    return () => {
      synth.cancel();
      end();
    };
  },
};
